import api, { isNotFoundError } from '@/services/api';
import type {
  InvoicingMode,
  SecurityGroupRecord,
  SecurityGroupDetail,
  SecurityGroupListFilters,
  SecurityGroupListResponse,
  AttachUsersPayload,
  UpdateUserPermissionsPayload,
  AssignUserToGroupsPayload,
  AssignUserToGroupsResponse,
  AttachableUsersResponse,
  SecurityDealerListResponse,
  SecurityProductListResponse,
  AssignableGroupsResponse,
} from '../types/security-admin.types';

export async function fetchSecurityGroups(filters: SecurityGroupListFilters = {}): Promise<SecurityGroupListResponse> {
  const { data } = await api.get<SecurityGroupListResponse>('/security/groups', { params: filters });
  return data;
}

export async function fetchSecurityGroup(id: string): Promise<SecurityGroupDetail> {
  const { data } = await api.get<{ data: SecurityGroupDetail }>(`/security/groups/${id}`);
  return data.data;
}

export async function createSecurityGroup(payload: { name: string; owner_dealer_id: string; invoicing_mode: InvoicingMode }): Promise<SecurityGroupRecord> {
  const { data } = await api.post<{ data: SecurityGroupRecord }>('/security/groups', payload);
  return data.data;
}

export async function updateSecurityGroup(id: string, payload: { name: string; invoicing_mode: InvoicingMode }): Promise<SecurityGroupRecord> {
  const { data } = await api.put<{ data: SecurityGroupRecord }>(`/security/groups/${id}`, payload);
  return data.data;
}

export async function deleteSecurityGroup(id: string): Promise<void> {
  await api.delete(`/security/groups/${id}`);
}

export async function attachDealersToGroup(
  id: string,
  vars: { dealer_ids: string[]; include_descendants?: boolean },
): Promise<void> {
  await api.post(`/security/groups/${id}/dealers`, vars);
}

export async function detachDealersFromGroup(id: string, dealerIds: string[]): Promise<void> {
  await api.delete(`/security/groups/${id}/dealers`, { data: { dealer_ids: dealerIds } });
}

export async function attachProductsToGroup(id: string, productIds: string[]): Promise<void> {
  await api.post(`/security/groups/${id}/products`, { product_ids: productIds });
}

export async function detachProductsFromGroup(id: string, productIds: string[]): Promise<void> {
  await api.delete(`/security/groups/${id}/products`, { data: { product_ids: productIds } });
}

export async function attachUsersToGroup(id: string, payload: AttachUsersPayload): Promise<void> {
  await api.post(`/security/groups/${id}/users`, payload);
}

export async function detachUsersFromGroup(id: string, userIds: string[]): Promise<void> {
  await api.delete(`/security/groups/${id}/users`, { data: { user_ids: userIds } });
}

export async function updateUserPermissions(
  groupId: string,
  userId: string,
  payload: UpdateUserPermissionsPayload,
): Promise<void> {
  await api.post(`/security/groups/${groupId}/users/${userId}/permissions`, payload);
}

export async function fetchSecurityGroupsForProduct(productId: string): Promise<SecurityGroupRecord[]> {
  const { data } = await api.get<{ success: boolean; data: SecurityGroupRecord[] }>(
    `/security/products/${productId}/groups`,
  );
  return data.data ?? [];
}

export async function fetchSecurityGroupsForDealer(dealerId: string): Promise<SecurityGroupRecord[]> {
  const { data } = await api.get<{ success: boolean; data: SecurityGroupRecord[] }>(
    `/security/dealers/${dealerId}/groups`,
  );
  return data.data ?? [];
}

export async function fetchSecurityGroupsForUser(userId: string): Promise<SecurityGroupRecord[]> {
  try {
    const { data } = await api.get<{ success: boolean; data: SecurityGroupRecord[] }>(
      `/security/users/${userId}/groups`,
    );
    return data.data ?? [];
  } catch (err) {
    // 404 means the user genuinely does not exist in auth-service.
    // Treat as empty list — the Attach button stays rendered so the operator
    // can still add groups; the first attach triggers BE lazy-create which
    // surfaces a proper 422 if the user truly doesn't exist.
    if (isNotFoundError(err)) {
      return [];
    }
    throw err;
  }
}

export async function fetchAttachableUsers(
  groupId: string,
  params: { search?: string; page?: number; per_page?: number },
): Promise<AttachableUsersResponse> {
  const response = await api.get<AttachableUsersResponse>(
    `/auth/users/attachable-for-group/${groupId}`,
    { params },
  );
  return response.data;
}

export async function assignSecurityGroupsToUser(
  userId: string,
  payload: AssignUserToGroupsPayload,
): Promise<AssignUserToGroupsResponse> {
  const { data } = await api.post<AssignUserToGroupsResponse>(
    `/security/users/${userId}/groups`,
    payload,
  );
  return data;
}

export async function fetchInScopeGroupsForProduct(
  productId: string,
  params: { search?: string; page?: number; per_page?: number } = {},
): Promise<SecurityGroupListResponse> {
  const { data } = await api.get<SecurityGroupListResponse>(
    `/security/products/${productId}/in-scope-groups`,
    { params },
  );
  return data;
}

export async function fetchScopedDealers(
  ownerDealerId: string,
  params: { search?: string; page?: number; per_page?: number } = {},
): Promise<SecurityDealerListResponse> {
  const { data } = await api.get<SecurityDealerListResponse>('/security/dealers', {
    params: { owner_dealer_id: ownerDealerId, ...params },
  });
  return data;
}

export async function fetchScopedProducts(
  ownerDealerId: string,
  params: { search?: string; page?: number; per_page?: number } = {},
): Promise<SecurityProductListResponse> {
  const { data } = await api.get<SecurityProductListResponse>('/security/products', {
    params: { owner_dealer_id: ownerDealerId, ...params },
  });
  return data;
}

export async function fetchAssignableGroups(
  userId: string,
  params: { search?: string; page?: number; per_page?: number } = {},
): Promise<AssignableGroupsResponse> {
  const { data } = await api.get<AssignableGroupsResponse>(
    `/security/users/${userId}/assignable-groups`,
    { params },
  );
  return data;
}

export async function fetchAssignableGroupsForDealer(
  dealerId: string,
  params: { search?: string; page?: number; per_page?: number } = {},
): Promise<AssignableGroupsResponse> {
  const { data } = await api.get<AssignableGroupsResponse>(
    `/security/dealers/${dealerId}/assignable-groups`,
    { params },
  );
  return data;
}
